"use client";

import { useTranslations } from "next-intl";

export interface GuidelineItem {
	label: string;
	value: string;
	notes?: string;
}

export interface GuidelineProps {
	translationNamespace?: string;
	title?: string;
	titleLine1?: string;
	titleLine2?: string;
	headerLabels?: {
		category?: string;
		recommendation?: string;
		notes?: string;
	};
	items?: GuidelineItem[];
	showNotes?: boolean;
}

const DEFAULT_GUIDELINE_KEYS = [
	{ labelKey: "minimumFeatureSize", valueKey: "minimumFeatureSizeValue" },
	{ labelKey: "linearDimensionTolerance", valueKey: "linearDimensionToleranceValue" },
	{ labelKey: "holeDiameters", valueKey: "holeDiametersValue" },
	{ labelKey: "shaftDiameters", valueKey: "shaftDiametersValue" },
	{ labelKey: "threadsAndTappedHoles", valueKey: "threadsAndTappedHolesValue" },
	{ labelKey: "engravedTextMarkings", valueKey: "engravedTextMarkingsValue" },
];

const CNC_MILLING_GUIDELINE_KEYS = [
	{ labelKey: "radii", valueKey: "radiiValue", notesKey: "radiiNotes" },
	{ labelKey: "threads", valueKey: "threadsValue", notesKey: "threadsNotes" },
	{ labelKey: "wallThickness", valueKey: "wallThicknessValue", notesKey: "wallThicknessNotes" },
	{ labelKey: "textEngraving", valueKey: "textEngravingValue", notesKey: "textEngravingNotes" },
	{ labelKey: "holes", valueKey: "holesValue", notesKey: "holesNotes" },
];

const CNC_TURNING_GUIDELINE_KEYS = [
	{ labelKey: "radii", valueKey: "radiiValue" },
	{ labelKey: "threads", valueKey: "threadsValue" },
	{ labelKey: "wallThickness", valueKey: "wallThicknessValue" },
	{ labelKey: "textEngraving", valueKey: "textEngravingValue" },
	{ labelKey: "holes", valueKey: "holesValue" },
];

export default function GurideLine({
	translationNamespace = "fiveAxisGuideline",
	title,
	titleLine1,
	titleLine2,
	headerLabels,
	items,
	showNotes = false,
}: GuidelineProps) {
	const t = useTranslations(translationNamespace);

	const useCustomData = !!items && items.length > 0;

	const getGuidelineKeys = () => {
		switch (translationNamespace) {
			case "cncMillingGuideline":
				return CNC_MILLING_GUIDELINE_KEYS;
			case "cncTurningGuideline":
				return CNC_TURNING_GUIDELINE_KEYS;
			default:
				return DEFAULT_GUIDELINE_KEYS;
		}
	};
	const guidelineKeys = getGuidelineKeys();

	const gridCols = showNotes
		? "grid-cols-1 sm:grid-cols-[25%_40%_35%] md:grid-cols-[20%_45%_35%]"
		: "grid-cols-1 sm:grid-cols-[30%_70%] md:grid-cols-[28%_72%]";

	const resolvedTitleLine1 = titleLine1 || (t.has("titleLine1") ? t("titleLine1") : title || t("title"));
	const resolvedTitleLine2 = titleLine2 || (t.has("titleLine2") ? t("titleLine2") : "");

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-shrink-0 flex-col items-start">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{resolvedTitleLine1}</span>
						<span className="flex items-end gap-4">
							{resolvedTitleLine2 && <span>{resolvedTitleLine2}</span>}
							<div className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
						</span>
					</h2>
				</div>

				<div className="mt-10 md:mt-14">
					<div className="w-full">
						<div className={`grid gap-4 border-b border-gray-200 pb-4 ${gridCols}`}>
							<div className="text-sm font-bold text-black md:text-base">
								{headerLabels?.category || t("designFeature") || t("guidelineCategory")}
							</div>
							<div className="text-sm font-bold text-black md:text-base">
								{headerLabels?.recommendation || t("recommendation") || t("recommendedSpecification")}
							</div>
							{showNotes && (
								<div className="text-sm font-bold text-black md:text-base">
									{headerLabels?.notes || t("notes") || "Notes"}
								</div>
							)}
						</div>

						{useCustomData &&
							items.map((item, index) => (
								<div key={index} className={`grid gap-4 border-b border-gray-200 py-5 md:py-6 ${gridCols}`}>
									<div className="text-sm font-bold text-black md:text-base">{item.label}</div>
									<div className="whitespace-pre-line text-sm leading-relaxed text-black md:text-base">
										{item.value}
									</div>
									{showNotes && (
										<div className="text-sm leading-relaxed text-black md:text-base">{item.notes || "-"}</div>
									)}
								</div>
							))}

						{!useCustomData &&
							guidelineKeys.map((item) => (
								<div
									key={item.labelKey}
									className={`grid gap-4 border-b border-gray-200 py-5 md:py-6 ${gridCols}`}
								>
									<div className="text-sm font-bold text-black md:text-base">{t(item.labelKey)}</div>
									<div className="whitespace-pre-line text-sm leading-relaxed text-black md:text-base">
										{t(item.valueKey)}
									</div>
									{showNotes && "notesKey" in item && (
										<div className="text-sm leading-relaxed text-black md:text-base">
											{t(item.notesKey as string) || "-"}
										</div>
									)}
								</div>
							))}
					</div>
				</div>
			</div>
		</section>
	);
}
